import { useMemo, useState } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TagCombobox } from '@/components/TagCombobox'
import SuggestCombobox from '@/components/admin/research/SuggestCombobox'
import ChildRowsSection, { type ChildColumn } from '@/components/admin/research/ChildRowsSection'
import CustomFieldsSection from '@/components/admin/research/CustomFieldsSection'
import { US_STATES } from '@/lib/usStates'
import { X } from 'lucide-react'
import type {
  FlyerAttachment,
  ResearchCommitteeMemberRow,
  ResearchCustomFieldDef,
  ResearchEventDetail,
  ResearchHonoreeRow,
  ResearchJournalAdRow,
  ResearchPackageRow,
  ResearchSponsorshipRow,
  ResearchSuggestions,
  ResearchTicketRow,
  ResearchVenueOption,
} from '@/types/research'

type Props = {
  event?: ResearchEventDetail
  organizations: { id: string; name: string }[]
  venues: ResearchVenueOption[]
  sponsorNames: string[]
  industries: string[]
  suggestions: ResearchSuggestions
  causes: string[]
  customFields: ResearchCustomFieldDef[]
  preselectedOrganizationId?: string | null
}

const GENDERS = ['Female', 'Male', 'Non-binary', 'N/A']
const RACES = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Native Hawaiian or Pacific Islander',
  'White',
  'Multiracial',
  'N/A',
]
const ETHNICITIES = ['Hispanic or Latino', 'Not Hispanic or Latino', 'N/A']
const HONOREE_TYPES = ['Individual', 'Corporate']
const JOURNAL_TYPES = ['Digital', 'Printed', 'Digital & Printed', 'Timed', 'Slide', 'NA']

type FormState = {
  research_organization_id: string
  venue_name: string
  venue_address: string
  venue_zip: string
  name: string
  event_type: string
  date: string
  start_time: string
  end_time: string
  about: string
  city: string
  state: string
  organization_budget: string
  ticket_tax_deduction: string
  event_link: string
  event_photo_video_url: string
  ticket_price: string
  goal: string
  email_service: string
  platform: string
  attire: string
  season: string
  gift_bag: string
  auction: string
  event_consultant: string
  dj_music: string
  photographer: string
  status: string
  emcee: string
  entertainment: string
  special_guest: string
  auctioneer: string
  estimated_attendance: string
  contact_name: string
  contact_title: string
  contact_email: string
  event_email: string
  gala_committee_members: ResearchCommitteeMemberRow[]
  host_committee_members: ResearchCommitteeMemberRow[]
  causes: string[]
  custom_data: Record<string, string | string[]>
  honorees: ResearchHonoreeRow[]
  sponsorships: ResearchSponsorshipRow[]
  sponsorship_packages: ResearchPackageRow[]
  ticket_tiers: ResearchTicketRow[]
  journal_ads: ResearchJournalAdRow[]
  event_image: File | null
  remove_event_image: boolean
  flyers: File[]
  remove_flyer_ids: string[]
}

function initialCustomData(
  fields: ResearchCustomFieldDef[],
  existing: Record<string, string | string[]> | undefined,
): Record<string, string | string[]> {
  const defaults = Object.fromEntries(
    fields.map(f => [f.key, f.field_type === 'tag' ? [] : (f.default_value ?? '')]),
  ) as Record<string, string | string[]>
  return { ...defaults, ...(existing ?? {}) }
}

function stripRow<Row extends { client_id?: string }>(row: Row, index: number) {
  const { client_id: _clientId, ...rest } = row
  return { ...rest, position: index }
}

function formatMoney(value: string) {
  const num = parseFloat(value)
  if (isNaN(num)) return null
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export default function ResearchEventForm({
  event,
  organizations,
  venues,
  sponsorNames,
  industries,
  suggestions,
  causes,
  customFields,
  preselectedOrganizationId,
}: Props) {
  const { data, setData, post, patch, processing, errors, transform } = useForm<{
    research_event: FormState
  }>({
    research_event: {
      research_organization_id: event?.research_organization_id ?? preselectedOrganizationId ?? '',
      venue_name: event?.venue_name ?? '',
      venue_address: event?.venue_address ?? '',
      venue_zip: event?.venue_zip ?? '',
      name: event?.name ?? '',
      event_type: event?.event_type ?? 'Gala',
      date: event?.date ?? '',
      start_time: event?.start_time ?? '',
      end_time: event?.end_time ?? '',
      about: event?.about ?? '',
      city: event?.city ?? '',
      state: event?.state ?? '',
      organization_budget: event?.organization_budget ?? '',
      ticket_tax_deduction: event?.ticket_tax_deduction ?? '',
      event_link: event?.event_link ?? '',
      event_photo_video_url: event?.event_photo_video_url ?? '',
      ticket_price: event?.ticket_price ?? '',
      goal: event?.goal ?? '',
      email_service: event?.email_service ?? '',
      platform: event?.platform ?? '',
      attire: event?.attire ?? '',
      season: event?.season ?? '',
      gift_bag: event?.gift_bag ?? '',
      auction: event?.auction ?? '',
      event_consultant: event?.event_consultant ?? '',
      dj_music: event?.dj_music ?? '',
      photographer: event?.photographer ?? '',
      status: event?.status ?? '',
      emcee: event?.emcee ?? '',
      entertainment: event?.entertainment ?? '',
      special_guest: event?.special_guest ?? '',
      auctioneer: event?.auctioneer ?? '',
      estimated_attendance: event?.estimated_attendance?.toString() ?? '',
      contact_name: event?.contact_name ?? '',
      contact_title: event?.contact_title ?? '',
      contact_email: event?.contact_email ?? '',
      event_email: event?.event_email ?? '',
      gala_committee_members: event?.gala_committee_members ?? [],
      host_committee_members: event?.host_committee_members ?? [],
      causes: event?.causes ?? [],
      custom_data: initialCustomData(customFields, event?.custom_data),
      honorees: event?.honorees ?? [],
      sponsorships: event?.sponsorships ?? [],
      sponsorship_packages: event?.sponsorship_packages ?? [],
      ticket_tiers: event?.ticket_tiers ?? [],
      journal_ads: event?.journal_ads ?? [],
      event_image: null,
      remove_event_image: false,
      flyers: [],
      remove_flyer_ids: [],
    },
  })

  const form = data.research_event
  const pageErrors = usePage().props.errors as Record<string, string | string[]> | undefined
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const existingFlyers: FlyerAttachment[] = event?.flyers ?? []

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setData('research_event', { ...data.research_event, [key]: value })
  }

  function fieldError(field: string): string | undefined {
    const formErr = errors[`research_event.${field}` as keyof typeof errors] as string | undefined
    if (formErr) return formErr
    const pageErr = pageErrors?.[field]
    return Array.isArray(pageErr) ? pageErr[0] : pageErr
  }

  function handleVenueChange(value: string) {
    const venue = venues.find(v => v.name.toLowerCase() === value.trim().toLowerCase())
    if (venue) {
      setData('research_event', {
        ...form,
        venue_name: value,
        venue_address: venue.address ?? '',
        venue_zip: venue.zip ?? '',
        city: venue.city ?? form.city,
        state: venue.state ?? form.state,
      })
    } else {
      setField('venue_name', value)
    }
  }

  const derivedDate = useMemo(() => {
    if (!form.date) return null
    const d = new Date(form.date + 'T00:00:00')
    if (isNaN(d.getTime())) return null
    return {
      month: d.toLocaleDateString('en-US', { month: 'long' }),
      day: d.toLocaleDateString('en-US', { weekday: 'long' }),
    }
  }, [form.date])

  const sponsorRevenue = form.sponsorships
    .filter(s => !s._destroy)
    .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0)

  // Sponsorship Type on sponsor rows offers this event's packages; picking one
  // copies its amount/tickets/tables onto the row.
  const tierSuggestions = [
    ...new Set(
      form.sponsorship_packages.filter(p => !p._destroy && p.name.trim()).map(p => p.name.trim()),
    ),
  ]

  function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setField('event_image', file)
    setImagePreview(file ? URL.createObjectURL(file) : null)
  }

  function handleFlyersChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) setField('flyers', [...form.flyers, ...files])
    e.target.value = ''
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    transform(formData => {
      const f = formData.research_event
      const {
        honorees, sponsorships, sponsorship_packages, ticket_tiers, journal_ads,
        gala_committee_members, host_committee_members,
        event_image, remove_event_image, ...rest
      } = f
      return {
        research_event: {
          ...rest,
          ...(event_image ? { event_image } : {}),
          remove_event_image: remove_event_image ? 'true' : 'false',
          honorees_attributes: honorees.map(stripRow),
          sponsorships_attributes: sponsorships.map(stripRow),
          sponsorship_packages_attributes: sponsorship_packages.map(stripRow),
          ticket_tiers_attributes: ticket_tiers.map(stripRow),
          journal_ads_attributes: journal_ads.map(stripRow),
          gala_committee_members_attributes: gala_committee_members.map(stripRow),
          host_committee_members_attributes: host_committee_members.map(stripRow),
        },
      }
    })
    if (event) {
      patch(`/admin/research/events/${event.id}`)
    } else {
      post('/admin/research/events')
    }
  }

  const committeeColumns: ChildColumn<ResearchCommitteeMemberRow>[] = [
    { key: 'name', label: 'Name' },
    { key: 'title', label: 'Title' },
    {
      key: 'industry',
      label: 'Professional Network',
      render: (row, update) => (
        <SuggestCombobox
          placeholder="e.g. Finance"
          suggestions={industries}
          value={row.industry}
          onChange={update}
        />
      ),
    },
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {pageErrors && Object.keys(pageErrors).length > 0 && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <p className="font-medium">Please fix the following:</p>
          <ul className="mt-1 list-inside list-disc">
            {Object.entries(pageErrors).map(([key, message]) => (
              <li key={key}>{Array.isArray(message) ? message[0] : message}</li>
            ))}
          </ul>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-org">
                Organization<span className="text-destructive"> *</span>
              </Label>
              <Select
                value={form.research_organization_id}
                onValueChange={value => setField('research_organization_id', value)}
              >
                <SelectTrigger id="event-org" className="w-full">
                  <SelectValue placeholder="Select organization..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldError('research_organization') && (
                <p className="text-xs text-destructive">{fieldError('research_organization')}</p>
              )}
              {organizations.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No research organizations yet — create one first.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-name">Event Name</Label>
              <Input
                id="event-name"
                value={form.name}
                onChange={e => setField('name', e.target.value)}
              />
              {fieldError('name') && <p className="text-xs text-destructive">{fieldError('name')}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SuggestCombobox
              label="Event Type"
              id="event-type"
              suggestions={suggestions.event_type ?? []}
              value={form.event_type}
              onChange={value => setField('event_type', value)}
            />
            <SuggestCombobox
              label="Status"
              id="event-status"
              placeholder="e.g. Confirmed, Researching..."
              suggestions={suggestions.status ?? []}
              value={form.status}
              onChange={value => setField('status', value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-date">Event Date</Label>
              <Input
                id="event-date"
                type="date"
                value={form.date}
                onChange={e => setField('date', e.target.value)}
              />
              {derivedDate && (
                <p className="text-xs text-muted-foreground">
                  {derivedDate.day} · {derivedDate.month}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-start">Start Time</Label>
              <Input
                id="event-start"
                type="time"
                value={form.start_time}
                onChange={e => setField('start_time', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-end">End Time</Label>
              <Input
                id="event-end"
                type="time"
                value={form.end_time}
                onChange={e => setField('end_time', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Location</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <SuggestCombobox
              label="Venue"
              id="event-venue"
              placeholder="Type a venue name..."
              suggestions={venues.map(v => v.name)}
              value={form.venue_name}
              onChange={handleVenueChange}
              helpText="Picking a known venue fills in the address below."
            />
            <div className="col-span-2 space-y-2">
              <Label htmlFor="event-venue-address">Address</Label>
              <Input
                id="event-venue-address"
                placeholder="Street address"
                value={form.venue_address}
                onChange={e => setField('venue_address', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-city">City</Label>
              <Input
                id="event-city"
                value={form.city}
                onChange={e => setField('city', e.target.value)}
              />
            </div>
            <SuggestCombobox
              label="State"
              id="event-state"
              suggestions={US_STATES}
              value={form.state}
              onChange={value => setField('state', value)}
            />
            <div className="space-y-2">
              <Label htmlFor="event-venue-zip">Zip Code</Label>
              <Input
                id="event-venue-zip"
                value={form.venue_zip}
                onChange={e => setField('venue_zip', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="event-about">About</Label>
            <Textarea
              id="event-about"
              rows={4}
              value={form.about}
              onChange={e => setField('about', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-link">Event Link</Label>
              <Input
                id="event-link"
                type="url"
                placeholder="https://..."
                value={form.event_link}
                onChange={e => setField('event_link', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-photo-video">Event Photo/Video URL</Label>
              <Input
                id="event-photo-video"
                type="url"
                placeholder="https://..."
                value={form.event_photo_video_url}
                onChange={e => setField('event_photo_video_url', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SuggestCombobox
              label="Attire"
              id="event-attire"
              placeholder="e.g. Black Tie"
              suggestions={suggestions.attire ?? []}
              value={form.attire}
              onChange={value => setField('attire', value)}
            />
            <SuggestCombobox
              label="Season"
              id="event-season"
              placeholder="e.g. Spring"
              suggestions={suggestions.season ?? []}
              value={form.season}
              onChange={value => setField('season', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-emcee">Emcee</Label>
              <Input
                id="event-emcee"
                value={form.emcee}
                onChange={e => setField('emcee', e.target.value)}
              />
            </div>
            <SuggestCombobox
              label="Entertainment"
              id="event-entertainment"
              suggestions={suggestions.entertainment ?? []}
              value={form.entertainment}
              onChange={value => setField('entertainment', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-special-guest">Special Guest</Label>
              <Input
                id="event-special-guest"
                value={form.special_guest}
                onChange={e => setField('special_guest', e.target.value)}
              />
            </div>
            <SuggestCombobox
              label="DJ / Music"
              id="event-dj"
              suggestions={suggestions.dj_music ?? []}
              value={form.dj_music}
              onChange={value => setField('dj_music', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-auctioneer">Auctioneer</Label>
              <Input
                id="event-auctioneer"
                value={form.auctioneer}
                onChange={e => setField('auctioneer', e.target.value)}
              />
            </div>
            <SuggestCombobox
              label="Auction"
              id="event-auction"
              placeholder="e.g. Live, Silent, None"
              suggestions={suggestions.auction ?? []}
              value={form.auction}
              onChange={value => setField('auction', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SuggestCombobox
              label="Gift Bag"
              id="event-gift-bag"
              suggestions={suggestions.gift_bag ?? []}
              value={form.gift_bag}
              onChange={value => setField('gift_bag', value)}
            />
            <div className="space-y-2">
              <Label htmlFor="event-attendance">Estimated Attendance</Label>
              <Input
                id="event-attendance"
                type="number"
                value={form.estimated_attendance}
                onChange={e => setField('estimated_attendance', e.target.value)}
              />
            </div>
          </div>

          <TagCombobox
            label="Causes"
            placeholder="Type to search causes..."
            suggestions={causes}
            selected={form.causes}
            onChange={items => setField('causes', items)}
            allowCustom
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Development Contact</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-contact-name">Name</Label>
              <Input
                id="event-contact-name"
                value={form.contact_name}
                onChange={e => setField('contact_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-contact-title">Title</Label>
              <Input
                id="event-contact-title"
                value={form.contact_title}
                onChange={e => setField('contact_title', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-contact-email">Email</Label>
              <Input
                id="event-contact-email"
                type="email"
                value={form.contact_email}
                onChange={e => setField('contact_email', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-event-email">Event Email</Label>
              <Input
                id="event-event-email"
                type="email"
                value={form.event_email}
                onChange={e => setField('event_email', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <ChildRowsSection<ResearchCommitteeMemberRow>
        title="Gala Committee"
        addLabel="Add Member"
        emptyText="No gala committee members added yet."
        rows={form.gala_committee_members}
        onChange={rows => setField('gala_committee_members', rows)}
        columns={committeeColumns}
        blankRow={() => ({ name: '', title: '', industry: '', position: 0 })}
        gridClassName="grid-cols-3"
      />

      <ChildRowsSection<ResearchCommitteeMemberRow>
        title="Host Committee"
        addLabel="Add Member"
        emptyText="No host committee members added yet."
        rows={form.host_committee_members}
        onChange={rows => setField('host_committee_members', rows)}
        columns={committeeColumns}
        blankRow={() => ({ name: '', title: '', industry: '', position: 0 })}
        gridClassName="grid-cols-3"
      />

      <Card>
        <CardHeader>
          <CardTitle>Money &amp; logistics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-org-budget">Organization Budget</Label>
              <Input
                id="event-org-budget"
                placeholder="e.g. $1M–$5M"
                value={form.organization_budget}
                onChange={e => setField('organization_budget', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-tax-deduction">Ticket Tax Deduction</Label>
              <Input
                id="event-tax-deduction"
                value={form.ticket_tax_deduction}
                onChange={e => setField('ticket_tax_deduction', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-ticket-price">Ticket Price ($)</Label>
              <Input
                id="event-ticket-price"
                type="number"
                step="0.01"
                value={form.ticket_price}
                onChange={e => setField('ticket_price', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="event-goal">Goal ($)</Label>
              <Input
                id="event-goal"
                type="number"
                step="0.01"
                value={form.goal}
                onChange={e => setField('goal', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SuggestCombobox
              label="Email Service"
              id="event-email-service"
              placeholder="e.g. Mailchimp"
              suggestions={suggestions.email_service ?? []}
              value={form.email_service}
              onChange={value => setField('email_service', value)}
            />
            <SuggestCombobox
              label="Platform"
              id="event-platform"
              placeholder="e.g. OneCause, Givebutter"
              suggestions={suggestions.platform ?? []}
              value={form.platform}
              onChange={value => setField('platform', value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <SuggestCombobox
              label="Event Consultant"
              id="event-consultant"
              suggestions={suggestions.event_consultant ?? []}
              value={form.event_consultant}
              onChange={value => setField('event_consultant', value)}
            />
            <SuggestCombobox
              label="Photographer"
              id="event-photographer"
              suggestions={suggestions.photographer ?? []}
              value={form.photographer}
              onChange={value => setField('photographer', value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Media</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="event-image">Event Image</Label>
            {(imagePreview || (event?.event_image_url && !form.remove_event_image)) && (
              <div className="relative w-fit">
                <img
                  src={imagePreview ?? event?.event_image_url ?? ''}
                  alt="Event"
                  className="h-40 rounded-xl object-cover"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute top-2 right-2 size-7"
                  title="Remove image"
                  onClick={() => {
                    setData('research_event', {
                      ...form,
                      event_image: null,
                      remove_event_image: true,
                    })
                    setImagePreview(null)
                  }}
                >
                  <X className="size-4" />
                </Button>
              </div>
            )}
            <Input id="event-image" type="file" accept="image/*" onChange={handleImageChange} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="event-flyers">Event Flyers and Forms</Label>
            {(existingFlyers.length > 0 || form.flyers.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {existingFlyers
                  .filter(flyer => !form.remove_flyer_ids.includes(flyer.id))
                  .map(flyer => (
                    <Badge key={flyer.id} variant="secondary" className="gap-1 pr-1">
                      <a href={flyer.url} target="_blank" rel="noreferrer" className="hover:underline">
                        {flyer.filename}
                      </a>
                      <button
                        type="button"
                        className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10"
                        title="Remove file"
                        onClick={() =>
                          setField('remove_flyer_ids', [...form.remove_flyer_ids, flyer.id])
                        }
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                {form.flyers.map((file, index) => (
                  <Badge key={`${file.name}-${index}`} variant="outline" className="gap-1 pr-1">
                    {file.name}
                    <button
                      type="button"
                      className="ml-0.5 rounded-full p-0.5 hover:bg-foreground/10"
                      title="Remove file"
                      onClick={() =>
                        setField('flyers', form.flyers.filter((_, i) => i !== index))
                      }
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <Input id="event-flyers" type="file" multiple onChange={handleFlyersChange} />
          </div>
        </CardContent>
      </Card>

      <ChildRowsSection<ResearchHonoreeRow>
        title="Honorees"
        addLabel="Add Honoree"
        emptyText="No honorees added yet."
        rows={form.honorees}
        onChange={rows => setField('honorees', rows)}
        columns={[
          { key: 'name', label: 'Name' },
          {
            key: 'honoree_type',
            label: 'Type',
            render: (row, update) => (
              <SuggestCombobox
                placeholder="Individual / Corporate"
                suggestions={HONOREE_TYPES}
                value={row.honoree_type}
                onChange={update}
              />
            ),
          },
          { key: 'company', label: 'Company' },
          {
            key: 'industry',
            label: 'Professional Network',
            render: (row, update) => (
              <SuggestCombobox
                placeholder="e.g. Finance"
                suggestions={industries}
                value={row.industry}
                onChange={update}
              />
            ),
          },
          {
            key: 'gender',
            label: 'Gender',
            render: (row, update) => (
              <SuggestCombobox suggestions={GENDERS} value={row.gender} onChange={update} />
            ),
          },
          {
            key: 'race',
            label: 'Race',
            render: (row, update) => (
              <SuggestCombobox suggestions={RACES} value={row.race} onChange={update} />
            ),
          },
          {
            key: 'ethnicity',
            label: 'Ethnicity',
            render: (row, update) => (
              <SuggestCombobox suggestions={ETHNICITIES} value={row.ethnicity} onChange={update} />
            ),
          },
          { key: 'award', label: 'Award' },
          { key: 'notes', label: 'Notes' },
        ]}
        blankRow={() => ({
          name: '', gender: '', race: '', ethnicity: '', honoree_type: '',
          company: '', award: '', industry: '', notes: '', position: 0,
        })}
        gridClassName="grid-cols-3"
      />

      <ChildRowsSection<ResearchSponsorshipRow>
        title="Sponsors"
        addLabel="Add Sponsor"
        emptyText="No sponsors added yet."
        headerExtra={
          <>
            <Badge variant="secondary">
              Total Sponsors: {form.sponsorships.filter(s => !s._destroy).length}
            </Badge>
            <Badge variant="secondary">
              Total Revenue: {sponsorRevenue > 0 ? formatMoney(String(sponsorRevenue)) : 'NA'}
            </Badge>
          </>
        }
        rows={form.sponsorships}
        onChange={rows => setField('sponsorships', rows)}
        columns={[
          {
            key: 'sponsor_name',
            label: 'Company / Name',
            render: (row, update) => (
              <SuggestCombobox
                placeholder="Company name..."
                suggestions={sponsorNames}
                value={row.sponsor_name}
                onChange={update}
              />
            ),
          },
          {
            key: 'tier',
            label: 'Sponsorship Type',
            render: (row, _update, updateRow) => (
              <SuggestCombobox
                placeholder="Select package..."
                suggestions={tierSuggestions}
                value={row.tier}
                onChange={value => {
                  const pkg = form.sponsorship_packages.find(
                    p => !p._destroy && p.name.trim().toLowerCase() === value.trim().toLowerCase(),
                  )
                  updateRow({
                    tier: value,
                    ...(pkg
                      ? {
                          amount: pkg.price || row.amount || '',
                          tickets: pkg.tickets || row.tickets || '',
                          tables: pkg.tables || row.tables || '',
                        }
                      : {}),
                  })
                }}
              />
            ),
          },
          {
            key: 'sponsor_industry',
            label: 'Professional Network',
            render: (row, update) => (
              <SuggestCombobox
                placeholder="e.g. Finance"
                suggestions={industries}
                value={row.sponsor_industry}
                onChange={update}
              />
            ),
          },
          { key: 'amount', label: 'Amount ($)', type: 'number' },
          { key: 'tickets', label: 'Tickets', type: 'number' },
          { key: 'tables', label: 'Tables', type: 'number' },
          { key: 'notes', label: 'Notes' },
        ]}
        blankRow={() => ({
          sponsor_name: '', tier: '', sponsor_industry: '', amount: '',
          tickets: '', tables: '', notes: '', position: 0,
        })}
        gridClassName="grid-cols-4"
      />

      <ChildRowsSection<ResearchPackageRow>
        title="Sponsorship Packages"
        addLabel="Add Package"
        emptyText="No sponsorship packages added yet."
        rows={form.sponsorship_packages}
        onChange={rows => setField('sponsorship_packages', rows)}
        columns={[
          { key: 'name', label: 'Package Name' },
          { key: 'price', label: 'Price ($)', type: 'number' },
          { key: 'tickets', label: 'Tickets', type: 'number' },
          { key: 'tables', label: 'Tables', type: 'number' },
          {
            key: 'description',
            label: 'Benefits',
            fullWidth: true,
            render: (row, update) => (
              <Textarea
                placeholder="What each sponsorship package includes…"
                value={row.description ?? ''}
                onChange={e => update(e.target.value)}
                rows={4}
              />
            ),
          },
        ]}
        blankRow={() => ({
          name: '', price: '', tickets: '', tables: '', description: '', position: 0,
        })}
        gridClassName="grid-cols-4"
      />

      <ChildRowsSection<ResearchTicketRow>
        title="Additional Tickets"
        addLabel="Add Ticket"
        emptyText="No additional tickets added yet."
        rows={form.ticket_tiers}
        onChange={rows => setField('ticket_tiers', rows)}
        columns={[
          { key: 'name', label: 'Ticket Name' },
          { key: 'price', label: 'Price ($)', type: 'number' },
          { key: 'quantity', label: 'Number of Tickets', type: 'number' },
          { key: 'description', label: 'Benefits / Details' },
        ]}
        blankRow={() => ({ name: '', price: '', quantity: '', description: '', position: 0 })}
        gridClassName="grid-cols-4"
      />

      <ChildRowsSection<ResearchJournalAdRow>
        title="Journal Ads"
        addLabel="Add Ad"
        emptyText="No journal ads added yet."
        rows={form.journal_ads}
        onChange={rows => setField('journal_ads', rows)}
        columns={[
          { key: 'name', label: 'Ad (e.g. Full Page)' },
          {
            key: 'journal_type',
            label: 'Journal Type',
            render: (row, update) => (
              <SuggestCombobox
                placeholder="Digital, Printed..."
                suggestions={JOURNAL_TYPES}
                value={row.journal_type}
                onChange={update}
              />
            ),
          },
          { key: 'price', label: 'Price ($)', type: 'number' },
          { key: 'description', label: 'Description' },
        ]}
        blankRow={() => ({ name: '', journal_type: '', price: '', description: '', position: 0 })}
        gridClassName="grid-cols-4"
      />

      <CustomFieldsSection
        fields={customFields}
        value={form.custom_data}
        onChange={(key, value) => setField('custom_data', { ...form.custom_data, [key]: value })}
      />

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={processing}>
          {event ? 'Save changes' : 'Create research entry'}
        </Button>
      </div>
    </form>
  )
}
